import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import { Modal, Button, AsyncSearchSelect } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
]

const WORK_TYPE_OPTIONS = [
  { value: 'corrective', label: 'Corrective Maintenance (CM/Repair)' },
  { value: 'preventive', label: 'Preventive Maintenance (PM)' },
  { value: 'setup_installation', label: 'Set-up/Installation' },
  { value: 'user_training', label: 'User Training' },
  { value: 'predictive', label: 'Predictive' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'other', label: 'Other' },
]

const CATEGORY_OPTIONS = [
  { value: 'biomedical', label: 'Biomedical' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'it', label: 'IT/ICT' },
  { value: 'facilities', label: 'Facilities' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'other', label: 'Other' },
]

const initialFormData = {
  title: '',
  description: '',
  asset_id: '',
  vehicle_id: '',
  work_type: 'corrective',
  category: 'biomedical',
  priority: 'medium',
  status: 'open',
  assigned_to_id: '',
  due_date: '',
  estimated_hours: '',
  extra_work_needed: '',  // Additional work to be done (UCI Job Card)
  notes: '',
}

const WorkOrderForm = ({ open, onClose, workOrder, onSuccess, defaultAssetId, vehicle }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [assets, setAssets] = useState([])

  const isEditing = Boolean(workOrder?.id)

  useEffect(() => {
    if (open) {
      fetchOptions()
      if (workOrder) {
        setFormData({
          title: workOrder.title || '',
          description: workOrder.description || '',
          asset_id: workOrder.asset?.id || workOrder.asset || '',
          vehicle_id: workOrder.vehicle?.id || workOrder.vehicle || '',
          work_type: workOrder.work_type || 'corrective',
          category: workOrder.category || 'biomedical',
          priority: workOrder.priority || 'medium',
          status: workOrder.status || 'open',
          assigned_to_id: workOrder.assigned_to?.id || workOrder.assigned_to || '',
          due_date: workOrder.due_date ? workOrder.due_date.split('T')[0] : '',
          estimated_hours: workOrder.estimated_hours || '',
          extra_work_needed: workOrder.extra_work_needed || '',
          notes: workOrder.notes || '',
        })
      } else {
        setFormData({
          ...initialFormData,
          asset_id: vehicle ? '' : (defaultAssetId || ''),
          vehicle_id: vehicle?.id || '',
        })
      }
      setErrors({})
    }
  }, [open, workOrder, defaultAssetId, vehicle])

  const fetchOptions = async () => {
    try {
      const assetsRes = await api.get('/assets/assets/')
      setAssets(assetsRes.data.results || assetsRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.title.trim()) newErrors.title = 'Title is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const payload = {
        ...formData,
        asset_id: formData.asset_id || null,
        vehicle_id: formData.vehicle_id || null,
        assigned_to_id: formData.assigned_to_id || null,
        estimated_hours: formData.estimated_hours || null,
        due_date: formData.due_date ? formData.due_date.split('T')[0] : null,
      }

      if (isEditing) {
        await api.patch(`/maintenance/work-orders/${workOrder.id}/`, payload)
      } else {
        await api.post('/maintenance/work-orders/', payload)
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving work order:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        // Show general error toast
        const errorMessage = error.response?.data?.detail
          || error.response?.data?.error
          || 'Failed to save work order. Please check the form and try again.'
        toast.error(errorMessage)
      } else {
        toast.error('Failed to save work order. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Work Order' : 'Create Work Order'}
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={Boolean(errors.title)}
            helperText={errors.title}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {vehicle ? (
            <TextField
              fullWidth
              label="Vehicle"
              value={`${vehicle.name || vehicle.vehicle_number}${vehicle.registration_number ? ` (${vehicle.registration_number})` : ''}`}
              InputProps={{ readOnly: true }}
              disabled
            />
          ) : (
            <FormControl fullWidth>
              <InputLabel>Asset</InputLabel>
              <Select
                name="asset_id"
                value={formData.asset_id}
                onChange={handleChange}
                label="Asset"
              >
                <MenuItem value="">None</MenuItem>
                {assets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {asset.name} ({asset.asset_tag})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Work Type</InputLabel>
            <Select
              name="work_type"
              value={formData.work_type}
              onChange={handleChange}
              label="Work Type"
            >
              {WORK_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
              label="Category"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              label="Priority"
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <AsyncSearchSelect
            endpoint="/common/users/"
            value={formData.assigned_to_id}
            onChange={(id) => setFormData((prev) => ({ ...prev, assigned_to_id: id }))}
            getOptionLabel={(user) => {
              const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Unknown'
              return user.email ? `${name} (${user.email})` : name
            }}
            label="Assign To"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Due Date"
            name="due_date"
            type="date"
            value={formData.due_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Estimated Hours"
            name="estimated_hours"
            type="number"
            value={formData.estimated_hours}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Extra Work Needed"
            name="extra_work_needed"
            value={formData.extra_work_needed}
            onChange={handleChange}
            multiline
            rows={2}
            helperText="Additional work to be done (for UCI Job Card)"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default WorkOrderForm
